import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Pencil, ArrowRight, ShieldCheck, MapPin, RefreshCw, Plus,
  MessageSquare, Sparkles,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import MeetingForm from "../components/MeetingForm";
import MeetingsBlock from "../components/customer-blocks/MeetingsBlock";
import LeadMediaBlock from "../components/LeadMediaBlock";
import VoiceTextarea from "../components/VoiceTextarea";
import ConvertFromButton from "../components/ConvertFromButton";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";
import { LEAD_PHASE_GROUPS } from "../lib/enums";
import LeadHeroSection from "./sales/sections/LeadHeroSection";
import LeadTimelineSection from "./sales/sections/LeadTimelineSection";

const ALL_PHASES = LEAD_PHASE_GROUPS.flatMap((g) => g.phases);
const phaseMeta = (k) => ALL_PHASES.find((p) => p.key === k) || { label: k, color: "#647087" };

// Phase-key → EntityShell status tone.
const phaseTone = (key) => {
  if (!key) return "slate";
  if (/won|converted|active/i.test(key)) return "green";
  if (/lost|dead|cancelled/i.test(key)) return "slate";
  if (/qualif|proposal|negotiat/i.test(key)) return "amber";
  if (/hot|engaged/i.test(key)) return "red";
  return "blue";
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["owner", "admin", "manager"].includes(user?.role);
  const [lead, setLead] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [comment, setComment] = useState("");
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [phaseSaving, setPhaseSaving] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("overview");

  const phone = customer?.phone || lead?.customer_phone || "";
  const email = customer?.email || lead?.customer_email || "";

  const load = useCallback(async () => {
    try {
      const t = await api.get(`/leads/${id}/timeline`);
      setLead(t.data.lead);
      setTimeline(t.data.items || []);
      const cid = t.data.lead.customer_id;
      if (cid) {
        const c = await api.get(`/customers/${cid}`).catch(() => ({ data: null }));
        setCustomer(c.data);
      } else {
        setCustomer(null);
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load lead"));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, { intervalMs: 45_000 });

  const updatePhase = async (next) => {
    setPhaseSaving(true);
    try {
      await api.patch(`/leads/${id}/phase`, { phase: next });
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail));
    } finally { setPhaseSaving(false); }
  };

  const postComment = async () => {
    const txt = (comment || "").trim();
    if (!txt) return;
    try {
      await api.post("/lead-comments", { lead_id: id, text: txt });
      setComment("");
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not post comment"));
    }
  };

  const convert = async () => {
    const { data } = await api.post(`/leads/${id}/convert`, {});
    navigate(`/jobcards/${data.id}`);
  };

  const promoteToCustomer = async () => {
    const cust = await api.post("/customers", {
      customer_type: "individual",
      name: lead.customer_name || lead.title,
      phone: lead.customer_phone || "",
      whatsapp: lead.customer_phone || "",
      email: lead.customer_email || "",
      status: "active",
    });
    await api.put(`/leads/${id}`, { ...lead, customer_id: cust.data.id });
    await load();
  };

  const planTrip = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const stop = {
        lead_id: id,
        title: lead?.customer_name || lead?.title || "Stop",
        address: lead?.physical_address ||
          [lead?.estate_reserve, lead?.nearest_town, lead?.province_area].filter(Boolean).join(", "),
        lat: lead?.lat ?? null,
        lng: lead?.lng ?? null,
        place_id: lead?.place_id || null,
        address_source: lead?.address_source || (lead?.lat && lead?.lng ? "places" : "manual"),
      };
      const { data } = await api.post("/trips", {
        title: `Trip · ${stop.title} · ${today}`,
        start_date: today,
        stops: [stop],
      });
      window.location.href = `/trips/${data.id}?from=lead/${id}`;
    } catch (_e) { /* noop */ }
  };

  const filteredTimeline = useMemo(() => timeline, [timeline]);

  if (!lead) {
    return (
      <div className="p-6 space-y-3">
        <div className="text-sm text-[#647087]">Loading lead…</div>
        {err && (
          <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-300">{err}</div>
        )}
      </div>
    );
  }

  const meta = phaseMeta(lead.phase);
  const headerActions = (
    <>
      <button
        type="button"
        onClick={() => navigate(`/leads?edit=${id}`)}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid="lead-detail-edit-btn"
      >
        <Pencil className="w-3.5 h-3.5" /> Edit
      </button>
      <Link
        to={`/leads/${id}/onboarding`}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid="lead-detail-onboarding-btn"
      >
        <ShieldCheck className="w-3.5 h-3.5 text-[#1FB6C1]" /> Onboarding
      </Link>
      <ConvertFromButton
        targets={["jobcard"]}
        onConvert={convert}
        testIdPrefix="lead-detail-convert"
      />
      <Link
        to={`/research/lead/${id}?from=lead/${id}`}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid="lead-detail-research-btn"
        title="Deep research & sales brief"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#1FB6C1]" /> Research
      </Link>
      <button
        type="button"
        onClick={planTrip}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid="lead-detail-trip-btn"
        title="Create a new trip with this lead as stop 1"
      >
        <MapPin className="w-3.5 h-3.5 text-[#A78BFA]" /> Trip
      </button>
      <button
        type="button"
        onClick={load}
        className="hv-btn hv-btn-ghost text-xs"
        title="Reload timeline"
        data-testid="lead-detail-refresh"
      >
        <RefreshCw className="w-3.5 h-3.5" />
      </button>
    </>
  );

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "comments", label: "Comments" },
    { key: "meetings", label: "Meetings" },
    { key: "media", label: "Media" },
    { key: "activity", label: "Activity", count: filteredTimeline.length || undefined },
  ];

  return (
    <EntityShell
      shortId={lead.external_ref ? `#${lead.external_ref}` : undefined}
      kindLabel="Lead"
      title={lead.title || "Lead"}
      status={{ label: meta.label, tone: phaseTone(lead.phase) }}
      customerLink={customer ? {
        label: `View customer · ${customer.name}`,
        to: `/customers/${customer.id}`,
      } : undefined}
      backTo="/leads"
      actions={headerActions}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      footerMeta={{
        createdByName: lead.created_by_name,
        createdAtDisplay: lead.created_at_display
          || (lead.created_at ? new Date(lead.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
        updatedByName: lead.updated_by_name,
        updatedAtDisplay: lead.updated_at_display
          || (lead.updated_at ? new Date(lead.updated_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
      }}
    >
      <div className="space-y-4" data-testid="lead-detail-page">
        {err && (
          <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-300">{err}</div>
        )}

        {tab === "overview" && (
          <LeadHeroSection
            lead={lead}
            customer={customer}
            phone={phone}
            email={email}
            phaseSaving={phaseSaving}
            onUpdatePhase={updatePhase}
            onOpenMeeting={() => { setTab("meetings"); setMeetingOpen(true); }}
            onPromoteToCustomer={promoteToCustomer}
          />
        )}

        {tab === "comments" && (
          <div className="hv-card p-3 space-y-2" data-testid="lead-detail-comment-composer">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#1fb6c1]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#a0aabf] font-bold">Add comment</span>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <VoiceTextarea
                  value={comment}
                  onChange={setComment}
                  placeholder="Drop a quick note or update on this lead… or tap the mic."
                  rows={3}
                  context="lead_activity"
                  testIdPrefix="lead-detail-comment-voice"
                  data-testid="lead-detail-comment-input"
                />
              </div>
              <button
                type="button"
                onClick={postComment}
                disabled={!comment.trim()}
                className="hv-btn hv-btn-primary"
                data-testid="lead-detail-comment-submit"
              >
                <Plus className="w-3.5 h-3.5" /> Post
              </button>
            </div>
            <p className="text-[10px] text-[#647087] pt-1">
              Comments appear in the Activity tab once posted.
            </p>
          </div>
        )}

        {tab === "meetings" && (
          <div className="space-y-4">
            {meetingOpen ? (
              <div className="hv-card p-4" data-testid="lead-detail-meeting-inline-form">
                <h3 className="font-display text-lg font-bold mb-3">Schedule meeting for lead</h3>
                <MeetingForm
                  defaultLeadId={id}
                  defaultCustomerId={lead.customer_id || ""}
                  onCreated={() => { setMeetingOpen(false); load(); }}
                  onCancel={() => setMeetingOpen(false)}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMeetingOpen(true)}
                className="hv-btn hv-btn-primary"
                data-testid="lead-detail-meeting-add"
              >
                <Plus className="w-3.5 h-3.5" /> New meeting
              </button>
            )}
            <MeetingsBlock leadId={id} />
          </div>
        )}

        {tab === "media" && (
          <LeadMediaBlock
            leadId={id}
            customerId={lead?.customer_id || null}
            canEdit
            isManager={isManager}
          />
        )}

        {tab === "activity" && (
          <>
            <LeadTimelineSection items={filteredTimeline} />
            <HistoryBlock entity={{ id, kind: "lead" }} />
          </>
        )}
      </div>
    </EntityShell>
  );
}
